/**
 * oathera-passport-governance task 14.1: the tenant governance settings (design §2, §7;
 * Requirements 2.7, 5.6, 7.5, 8.8, 17.8).
 *
 * An ADMIN sets/clears the tenant-default risk with a FRESH impact confirmation (the server refuses
 * `impact_changed` if the count moved since it was shown), and manages the owner-loss policy with the
 * human-readable description of each option and the delegate target when `delegate` is chosen. A
 * failed write never updates optimistically; a successful write invalidates the settings query.
 */
import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OtpInput } from "../../../components/OtpInput.tsx";
import { computePreflightDigest } from "./preflight-digest.ts";
import {
  OWNER_LOSS_POLICY_DESCRIPTIONS,
  RISK_LEVELS,
  cancelActivationPrepare,
  clearTenantDefaultRisk,
  getActivationPreflight,
  getAgentGovernance,
  govErrorMessage,
  runActivationPrepare,
  runCanonicalBackfill,
  setOwnerLossPolicy,
  setTenantDefaultRisk,
  type ActivationPreflight,
  type BackfillReport,
  type GovResult,
  type OwnerLossPolicy,
  type PrepareResult,
  type RiskLevel,
  type TenantGovernanceState,
} from "./passport-governance-client.ts";

const HEX64 = /^[0-9a-f]{64}$/u;

// The tenant-settings read shape lives with the client that fetches it; re-exported here so the
// screen's existing consumers (and its tests) keep importing it from this module.
export type { TenantGovernanceState };

export interface PassportGovernanceSettingsProps {
  /** The fresh tenant settings (availability + counts). The caller fetches it (a GET read route). */
  readonly fetchSettings: (signal?: AbortSignal) => Promise<GovResult<TenantGovernanceState>>;
  /**
   * Whether to render the superadmin-only "Governance activation" section (canonical backfill +
   * activation preflight). The BFF reserves those routes to a superadmin regardless; the caller
   * hides the controls from non-superadmins so they never see an action they cannot take. Defaults
   * to `true` so existing callers (and the task-14.1 tests) keep the full screen.
   */
  readonly showActivation?: boolean;
}

const SETTINGS_KEY = ["passport-governance", "tenant-settings"] as const;

export function PassportGovernanceSettings({
  fetchSettings,
  showActivation = true,
}: PassportGovernanceSettingsProps): React.JSX.Element {
  const client = useQueryClient();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState<OwnerLossPolicy>("revoke");
  const mfaId = useId();
  const [mfaCode, setMfaCode] = useState("");
  const [backfill, setBackfill] = useState<BackfillReport | null>(null);
  const [preflight, setPreflight] = useState<ActivationPreflight | null>(null);
  // Prepare-activation sub-section (rollout task 21.2). The governance two-factor code is a SEPARATE
  // OtpInput from the backfill one (its own useId + state), so neither single-use code leaks into
  // the other action.
  const prepareMfaId = useId();
  const [prepareMfaCode, setPrepareMfaCode] = useState("");
  const [kyaAckDigest, setKyaAckDigest] = useState("");
  const [approvalRef, setApprovalRef] = useState("");
  // The next generation (current latest is 0). Shown to the operator; documented as the next gen.
  const [requestedGeneration, setRequestedGeneration] = useState(1);
  // The computed preflight digest, surfaced read-only so the operator can record it. Never typed.
  const [prepareDigest, setPrepareDigest] = useState<string | null>(null);
  const [prepareResult, setPrepareResult] = useState<PrepareResult | null>(null);

  // Keep `getAgentGovernance` referenced so the shared client module stays imported in type-checks
  // even when this screen only drives the tenant routes.
  void getAgentGovernance;

  const q = useQuery<GovResult<TenantGovernanceState>>({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) => fetchSettings(signal),
  });
  const state = q.data?.ok ? q.data.body : null;

  const invalidate = (): Promise<void> =>
    client.invalidateQueries({ queryKey: SETTINGS_KEY }).then(() => undefined);

  async function run(fn: () => Promise<GovResult<unknown>>): Promise<void> {
    if (reason.trim().length === 0 || reason.length > 500) {
      setError("A reason of 1 to 500 characters is required.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await fn();
    setBusy(false);
    if (result.ok) {
      setReason("");
      await invalidate();
    } else {
      // `impact_changed` tells the operator the fresh count moved; the next read shows it and they
      // confirm again (the write is never applied against a stale count).
      setError(govErrorMessage(result.code));
    }
  }

  // oathera-passport-governance rollout tasks 20.1 and 21.2: the flag-off-safe canonical backfill
  // (superadmin + governance step-up) and the read-only activation preflight. The backfill stages
  // canonical principals only; the active pair and every signed Passport stay byte-identical.
  async function onRunBackfill(): Promise<void> {
    if (reason.trim().length === 0 || reason.length > 500) {
      setError("A reason of 1 to 500 characters is required.");
      return;
    }
    if (mfaCode.length !== 6) {
      setError("Enter a current 6-digit two-factor code.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await runCanonicalBackfill({ reason, mfaCode });
    setBusy(false);
    if (result.ok) {
      // Keep the reason and the result visible; only clear the single-use code. The report is held
      // in state and rendered in the persistent "Activation results" region (never a transient line).
      setMfaCode("");
      setBackfill(result.body);
    } else {
      // A `mfa_required`/`mfa_enrollment_required` failure clears the spent code so the operator
      // re-enters a fresh one; every other code keeps the entry for correction.
      if (result.code === "mfa_required" || result.code === "mfa_enrollment_required") {
        setMfaCode("");
      }
      setError(govErrorMessage(result.code));
    }
  }

  async function onCheckPreflight(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await getActivationPreflight();
    setBusy(false);
    // The preflight returns 409 with a well-formed report when a blocker exists; show the report.
    if (result.ok) {
      setPreflight(result.body);
    } else if (result.status === 409) {
      setError("Activation is blocked by one or more conflicts; see the preflight report.");
    } else {
      setError(govErrorMessage(result.code));
    }
  }

  // Run the preflight and, on a usable report, derive and surface the canonical preflightDigest. The
  // operator records the shown value; it is fed into prepare automatically (never hand-typed).
  async function onComputePreflightDigest(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await getActivationPreflight();
    if (!result.ok && result.status !== 409) {
      setBusy(false);
      setError(govErrorMessage(result.code));
      return;
    }
    // The preflight returns a well-formed report on 200 and on a 409 blocker; digest either so the
    // operator can see the fingerprint of the current state regardless.
    const report = result.ok ? result.body : null;
    if (report === null) {
      setBusy(false);
      setError("Activation is blocked by one or more conflicts; see the preflight report.");
      return;
    }
    const digest = await computePreflightDigest(report);
    setBusy(false);
    setPreflight(report);
    setPrepareDigest(digest);
  }

  async function onPrepareActivation(): Promise<void> {
    if (reason.trim().length === 0 || reason.length > 500) {
      setError("A reason of 1 to 500 characters is required.");
      return;
    }
    if (!HEX64.test(kyaAckDigest)) {
      setError("Enter the 64-character hex KYA acknowledgement digest.");
      return;
    }
    if (approvalRef.length < 1 || approvalRef.length > 128) {
      setError("Enter an approval reference of 1 to 128 characters.");
      return;
    }
    if (prepareDigest === null) {
      setError("Compute the preflight digest before preparing activation.");
      return;
    }
    if (prepareMfaCode.length !== 6) {
      setError("Enter a current 6-digit two-factor code.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await runActivationPrepare({
      reason,
      mfaCode: prepareMfaCode,
      requestedGeneration,
      approvalRef,
      kyaAckDigest,
      preflightDigest: prepareDigest,
    });
    setBusy(false);
    if (result.ok) {
      // Clear only the single-use code; keep the result in the persistent results region.
      setPrepareMfaCode("");
      setPrepareResult(result.body);
    } else {
      if (result.code === "mfa_required" || result.code === "mfa_enrollment_required") {
        setPrepareMfaCode("");
      }
      setError(govErrorMessage(result.code));
    }
  }

  async function onCancelPrepare(): Promise<void> {
    if (reason.trim().length === 0 || reason.length > 500) {
      setError("A reason of 1 to 500 characters is required.");
      return;
    }
    if (prepareMfaCode.length !== 6) {
      setError("Enter a current 6-digit two-factor code.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await cancelActivationPrepare({ reason, mfaCode: prepareMfaCode });
    setBusy(false);
    if (result.ok) {
      setPrepareMfaCode("");
      setPrepareResult(null);
    } else {
      if (result.code === "mfa_required" || result.code === "mfa_enrollment_required") {
        setPrepareMfaCode("");
      }
      setError(govErrorMessage(result.code));
    }
  }

  if (q.isLoading)
    return (
      <section aria-label="Tenant governance settings">
        <p>Loading…</p>
      </section>
    );

  const confirmedCount = state?.unoverriddenAgentCount ?? 0;
  const defaultVersion = state?.defaultRecordVersion ?? 0;
  const policyVersion = state?.policyVersion ?? 0;

  return (
    <section aria-label="Tenant governance settings">
      <h3>Tenant governance</h3>
      {error !== null && (
        <p role="alert" data-testid="settings-error">
          {error}
        </p>
      )}

      <label htmlFor={reasonId}>Reason</label>
      <input
        id={reasonId}
        value={reason}
        maxLength={500}
        onChange={(e) => setReason(e.target.value)}
      />

      <fieldset>
        <legend>Default risk</legend>
        <p data-testid="impact-count">
          This change applies to {confirmedCount} agent{confirmedCount === 1 ? "" : "s"} without an
          override.
        </p>
        <div role="group" aria-label="Set default risk">
          {RISK_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              disabled={busy}
              onClick={() =>
                run(() => setTenantDefaultRisk(level as RiskLevel, confirmedCount, { reason }))
              }
            >
              Set default {level}
            </button>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => clearTenantDefaultRisk(confirmedCount, { reason }))}
          >
            Clear default (version {defaultVersion})
          </button>
        </div>
      </fieldset>

      <fieldset>
        <legend>Owner-loss policy</legend>
        <p data-testid="owned-count">{state?.ownedCount ?? 0} agents currently have an owner.</p>
        <select
          data-testid="policy-select"
          value={policy}
          onChange={(e) => setPolicy(e.target.value as OwnerLossPolicy)}
        >
          {(Object.keys(OWNER_LOSS_POLICY_DESCRIPTIONS) as OwnerLossPolicy[]).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <p data-testid="policy-description">{OWNER_LOSS_POLICY_DESCRIPTIONS[policy]}</p>
        {state?.ownerLossPolicy === "delegate" && (
          <p data-testid="delegate-display">
            {state.delegateDisplay === null
              ? "Delegated automatically"
              : `Delegate: ${state.delegateDisplay}`}
          </p>
        )}
        <button
          type="button"
          disabled={busy || policy === "delegate"}
          onClick={() => run(() => setOwnerLossPolicy(policy, policyVersion, { reason }))}
        >
          Save policy
        </button>
        {policy === "delegate" && (
          <p data-testid="delegate-required">
            Choose a delegate member on the owner route to use the delegate policy.
          </p>
        )}
      </fieldset>

      {showActivation && (
        <>
          <fieldset>
            <legend>Governance activation (superadmin)</legend>
            <p data-testid="activation-help">
              Run the canonical backfill to stage each agent&apos;s canonical principal, then check
              the activation preflight. The backfill never changes an issued Passport; it only
              stages the canonical pair. Both are safe while governance is off.
            </p>
            <label htmlFor={mfaId}>Two-factor code (backfill)</label>
            <OtpInput id={mfaId} value={mfaCode} onChange={setMfaCode} required align="start" />
            <div role="group" aria-label="Governance activation actions">
              <button
                type="button"
                data-testid="run-backfill"
                disabled={busy || mfaCode.length !== 6 || reason.trim().length === 0}
                onClick={() => onRunBackfill()}
              >
                Run canonical backfill
              </button>
              <button
                type="button"
                data-testid="check-preflight"
                disabled={busy}
                onClick={() => onCheckPreflight()}
              >
                Check activation preflight
              </button>
            </div>
            {(backfill !== null || preflight !== null || prepareResult !== null) && (
              <div
                className="svc-admin__section"
                data-testid="activation-results"
                aria-live="polite"
              >
                <div className="svc-adm2__card-head">
                  <h4 className="svc-admin__section-title">Activation results</h4>
                  <button
                    type="button"
                    className="svc-admin__btn"
                    data-testid="clear-activation-results"
                    onClick={() => {
                      setBackfill(null);
                      setPreflight(null);
                      setPrepareResult(null);
                    }}
                  >
                    Clear
                  </button>
                </div>
                {backfill !== null && (
                  <p data-testid="backfill-report">
                    Backfill scanned {backfill.scanned}: {backfill.ready} ready, {backfill.unlinked}{" "}
                    unlinked, {backfill.conflicts.length} conflict
                    {backfill.conflicts.length === 1 ? "" : "s"}, {backfill.deferred.length}{" "}
                    deferred
                    {backfill.retryable ? " (re-run to retry deferred agents)" : ""}.
                  </p>
                )}
                {preflight !== null && (
                  <p data-testid="preflight-report">
                    Preflight:{" "}
                    {preflight.ok ? "no blockers" : `${preflight.blockers.length} blocker(s)`};{" "}
                    {preflight.immediateManagerChangeCount} immediate manager change
                    {preflight.immediateManagerChangeCount === 1 ? "" : "s"},{" "}
                    {preflight.deferredManagerChangeCount} deferred; risk-affected{" "}
                    {preflight.riskAffectedCount}, owner-affected {preflight.ownerAffectedCount}.
                  </p>
                )}
                {prepareResult !== null && (
                  <p data-testid="prepare-report">
                    Prepared activation {prepareResult.opId}: next generation{" "}
                    {prepareResult.next_generation}, governance writes{" "}
                    {prepareResult.blocked_writes ? "blocked" : "not blocked"} until the intent
                    commits or is cancelled.
                  </p>
                )}
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend>Prepare activation (superadmin)</legend>
            <p data-testid="prepare-help">
              Record a <code>preparing</code> activation intent for the next generation. Compute the
              preflight digest first; it is derived from the current preflight report and fed into
              prepare automatically. Preparing BLOCKS governance writes for the tenant until the
              intent commits or is cancelled; it seals nothing and changes no production flag. The
              current KYA acknowledgement digest is{" "}
              <code>64bcc1cdb09115b0ffce71b9925b21551f99b8441a5bff9635aaa8480c83dfc4</code>.
            </p>
            <label htmlFor="pg-requested-generation">Requested generation (next generation)</label>
            <input
              id="pg-requested-generation"
              type="number"
              data-testid="requested-generation"
              value={requestedGeneration}
              min={1}
              onChange={(e) => setRequestedGeneration(Number(e.target.value))}
            />
            <label htmlFor="pg-kya-ack-digest">KYA acknowledgement digest (64 hex)</label>
            <input
              id="pg-kya-ack-digest"
              data-testid="kya-ack-digest"
              value={kyaAckDigest}
              maxLength={64}
              placeholder="64bcc1cdb09115b0ffce71b9925b21551f99b8441a5bff9635aaa8480c83dfc4"
              onChange={(e) => setKyaAckDigest(e.target.value.trim())}
            />
            <label htmlFor="pg-approval-ref">Approval reference (1–128 chars)</label>
            <input
              id="pg-approval-ref"
              data-testid="approval-ref"
              value={approvalRef}
              maxLength={128}
              onChange={(e) => setApprovalRef(e.target.value)}
            />
            <label htmlFor={prepareMfaId}>Two-factor code (prepare)</label>
            <OtpInput
              id={prepareMfaId}
              value={prepareMfaCode}
              onChange={setPrepareMfaCode}
              required
              align="start"
            />
            <div role="group" aria-label="Prepare activation actions">
              <button
                type="button"
                data-testid="compute-prepare-digest"
                disabled={busy}
                onClick={() => onComputePreflightDigest()}
              >
                Compute preflight digest
              </button>
              <button
                type="button"
                data-testid="prepare-activation"
                disabled={
                  busy ||
                  reason.trim().length === 0 ||
                  !HEX64.test(kyaAckDigest) ||
                  approvalRef.length < 1 ||
                  approvalRef.length > 128 ||
                  prepareMfaCode.length !== 6 ||
                  prepareDigest === null
                }
                onClick={() => onPrepareActivation()}
              >
                Prepare activation
              </button>
              <button
                type="button"
                data-testid="cancel-prepare"
                disabled={busy || reason.trim().length === 0 || prepareMfaCode.length !== 6}
                onClick={() => onCancelPrepare()}
              >
                Cancel preparing intent
              </button>
            </div>
            {prepareDigest !== null && (
              <p>
                Preflight digest: <code data-testid="prepare-digest">{prepareDigest}</code>
              </p>
            )}
          </fieldset>
        </>
      )}
    </section>
  );
}
